import { Box, Divider, Stack, TextField, Typography } from '@mui/material';
import { type AccountInput, type NewAccount } from '@vy/types';
import { countries } from 'countries-list';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { CountrySelect } from '../../../../../../components/new-design/inputs/CountrySelect';

export const BankInfoSection: React.FC = () => {
  const {
    control,
    register,
    formState: { defaultValues, errors },
  } = useFormContext<AccountInput, unknown, NewAccount>();

  const registerBankName = register('settings.bankDetails.bankName');
  const registerRoutingNumber = register('settings.bankDetails.routingNumber');
  const registerAccountNumber = register('settings.bankDetails.accountNumber');
  const registerCompanyName = register('settings.bankDetails.companyName');
  const registerStreetAddress = register('settings.bankDetails.address.addressText');
  const registerCity = register('settings.bankDetails.address.city');
  const registerState = register('settings.bankDetails.address.state');
  const registerPostalCode = register('settings.bankDetails.address.postalCode');
  const registerCountry = register('settings.bankDetails.address.country');
  const registerSwiftCode = register('settings.bankDetails.swiftCode');
  const registerAbaNumber = register('settings.bankDetails.abaNumber');
  const registerSortCode = register('settings.bankDetails.sortCode');

  return (
    <Stack divider={<Divider sx={{ marginY: '20px' }} />}>
      <Stack
        direction="column"
        sx={{
          display: "flex",
          paddingX: "20px"
        }}>
        <Typography variant="h6">Bank Info</Typography>
        <Typography variant="subtitle1" sx={{
          color: "text.secondary"
        }}>
          Update your banking details here
        </Typography>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          display: "flex",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          Bank name
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerBankName.name}
            defaultValue={defaultValues?.settings?.bankDetails?.bankName ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                error={!!errors?.settings?.bankDetails?.bankName}
                helperText={errors?.settings?.bankDetails?.bankName?.message}
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          display: "flex",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          Company name
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerCompanyName.name}
            defaultValue={defaultValues?.settings?.bankDetails?.companyName ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                error={!!errors?.settings?.bankDetails?.companyName}
                helperText={errors?.settings?.bankDetails?.companyName?.message}
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          Street address
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerStreetAddress.name}
            defaultValue={defaultValues?.settings?.bankDetails?.address?.addressText ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                error={!!errors?.settings?.bankDetails?.address?.addressText}
                helperText={errors?.settings?.bankDetails?.address?.addressText?.message}
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          Country
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerCountry.name}
            defaultValue={defaultValues?.settings?.bankDetails?.address?.country ?? ''}
            render={({ field }) => (
              <CountrySelect
                value={getCountryByName(field.value ?? '')}
                textFieldProps={field}
                onChange={(country) => {
                  field.onChange(country?.name);
                }}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          City
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerCity.name}
            defaultValue={defaultValues?.settings?.bankDetails?.address?.city ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                error={!!errors?.settings?.bankDetails?.address?.city}
                helperText={errors?.settings?.bankDetails?.address?.city?.message}
                fullWidth
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          State / Province
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
            columnGap: '16px',
          }}
        >
          <Controller
            control={control}
            name={registerState.name}
            defaultValue={defaultValues?.settings?.bankDetails?.address?.state ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                placeholder="State"
                error={!!errors?.settings?.bankDetails?.address?.state}
                helperText={errors?.settings?.bankDetails?.address?.state?.message}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name={registerPostalCode.name}
            defaultValue={defaultValues?.settings?.bankDetails?.address?.postalCode ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                placeholder="ZIP / Postal code"
                error={!!errors?.settings?.bankDetails?.address?.postalCode}
                helperText={errors?.settings?.bankDetails?.address?.postalCode?.message}
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          display: "flex",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          IBAN (routing)
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerRoutingNumber.name}
            defaultValue={defaultValues?.settings?.bankDetails?.routingNumber ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                error={!!errors?.settings?.bankDetails?.routingNumber}
                helperText={errors?.settings?.bankDetails?.routingNumber?.message}
                fullWidth
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          display: "flex",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          Swift code
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerSwiftCode.name}
            defaultValue={defaultValues?.settings?.bankDetails?.swiftCode ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                error={!!errors?.settings?.bankDetails?.swiftCode}
                helperText={errors?.settings?.bankDetails?.swiftCode?.message}
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          display: "flex",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          Account number
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerAccountNumber.name}
            defaultValue={defaultValues?.settings?.bankDetails?.accountNumber ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                error={!!errors?.settings?.bankDetails?.accountNumber}
                helperText={errors?.settings?.bankDetails?.accountNumber?.message}
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          display: "flex",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          ABA
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerAbaNumber.name}
            defaultValue={defaultValues?.settings?.bankDetails?.abaNumber ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                error={!!errors?.settings?.bankDetails?.abaNumber}
                helperText={errors?.settings?.bankDetails?.abaNumber?.message}
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          maxWidth: "md",
          display: "flex",
          paddingX: "20px"
        }}>
        <Typography
          sx={{
            display: "flex",
            flex: 7
          }}>
          Sort code
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flex: 8,
          }}
        >
          <Controller
            control={control}
            name={registerSortCode.name}
            defaultValue={defaultValues?.settings?.bankDetails?.sortCode ?? ''}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                error={!!errors?.settings?.bankDetails?.sortCode}
                helperText={errors?.settings?.bankDetails?.sortCode?.message}
                {...field}
              />
            )}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

function getCountryByName(countryName: string) {
  return Object.values(countries).find((country) => country.name === countryName) ?? null;
}
